import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, KeyRound, Lock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import {
  Detail,
  DetailList,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
} from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { Field, Input, Select, Switch } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { Callout } from '@/ui/Callout';
import { ConfirmDialog } from '@/ui/Dialog';
import { api, errorMessage, ApiError } from '@/lib/api/client';
import { useAtLeast, useAuth } from '@/lib/auth';
import { dateTime, humanize, relativeTime } from '@/lib/format';
import {
  fullName,
  roleTone,
  staffPermissions,
  validatePassword,
  validatePin,
} from '@/features/system/staff';
import type { UserSummary } from '@/lib/api/types';

/**
 * One staff account.
 *
 * Every control here is shadowed by a server-side escalation guard. Where a
 * guard applies, the control is rendered disabled *with its reason* rather than
 * hidden — a manager who cannot reset an admin's password needs to understand
 * that, not wonder where the field went.
 */

export const Route = createFileRoute('/_app/system/staff_/$id')({
  staticData: { crumb: 'User' },
  component: StaffDetailScreen,
});

interface ProfileDraft {
  firstName: string;
  lastName: string;
  role: string;
  active: boolean;
}

function StaffDetailScreen() {
  const { id } = Route.useParams();
  const userId = Number(id);
  const queryClient = useQueryClient();
  const { user: actor } = useAuth();
  const canWrite = useAtLeast('manager');

  // `GET /users/:id` does not exist — the list is the only read, and it is one
  // small query the shell already caches.
  const staff = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/users'),
  });

  const target = staff.data?.find((u) => u.id === userId) ?? null;

  const permissions = useMemo(
    () =>
      target
        ? staffPermissions(
            target,
            actor ? { id: actor.id, role: String(actor.role) } : null
          )
        : null,
    [target, actor]
  );

  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const current: ProfileDraft | null = target
    ? {
        firstName: target.firstName ?? '',
        lastName: target.lastName ?? '',
        role: target.role,
        active: target.active,
      }
    : null;
  const value = draft ?? current;

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put<UserSummary>(`/users/${userId}`, body),
    onSuccess: (_updated, body) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      // Editing your own row changes what the shell shows you.
      if (actor && actor.id === userId) {
        void queryClient.invalidateQueries({ queryKey: ['session'] });
      }
      setDraft(null);
      setPassword('');
      setPin('');
      setConfirmDeactivate(false);
      toast.success('Saved', {
        description: body.pin
          ? 'Profile updated and the register PIN replaced.'
          : body.password
            ? 'Profile updated and the password reset.'
            : 'Profile updated.',
      });
    },
    onError: (error) => {
      setConfirmDeactivate(false);
      const forbidden = error instanceof ApiError && error.status === 403;
      const conflict = error instanceof ApiError && error.status === 409;
      toast.error(
        forbidden
          ? 'Refused by the server'
          : conflict
            ? 'PIN already in use'
            : 'Save failed',
        { description: errorMessage(error) }
      );
    },
  });

  if (staff.isLoading) {
    return (
      <Page>
        <PageHeader eyebrow="System · Staff" title="Loading…" />
        <PageBody className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (staff.isError) {
    return (
      <Page>
        <PageHeader eyebrow="System · Staff" title={`User ${id}`} />
        <PageBody>
          <ErrorState
            message={errorMessage(staff.error)}
            onRetry={() => void staff.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  if (!target || !value || !current || !permissions) {
    return (
      <Page>
        <PageHeader eyebrow="System · Staff" title={`User ${id}`} />
        <PageBody>
          <ErrorState title="No such user" message={`No staff account with id ${id}.`} />
        </PageBody>
      </Page>
    );
  }

  const passwordError = validatePassword(password);
  const pinError = validatePin(pin);
  const profileDirty =
    value.firstName !== current.firstName ||
    value.lastName !== current.lastName ||
    value.role !== current.role ||
    value.active !== current.active;
  const dirty = profileDirty || password.length > 0 || pin.length > 0;
  const blocked = !canWrite || !permissions.canEdit;
  const invalid = !!passwordError || !!pinError || value.firstName.trim().length === 0;

  function buildBody(): Record<string, unknown> {
    if (!value || !current) return {};
    const body: Record<string, unknown> = {};
    if (value.firstName !== current.firstName) body.firstName = value.firstName;
    if (value.lastName !== current.lastName) body.lastName = value.lastName;
    if (value.role !== current.role) body.role = value.role;
    if (value.active !== current.active) body.active = value.active;
    if (password) body.password = password;
    if (pin) body.pin = pin;
    return body;
  }

  function submit() {
    // Deactivating an account locks somebody out of the register mid-shift.
    if (value && current && value.active === false && current.active === true) {
      setConfirmDeactivate(true);
      return;
    }
    save.mutate(buildBody());
  }

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link
            to="/system/staff"
            search={{ q: '', view: 'all' }}
            className="hover:text-ink hover:underline"
          >
            System · Staff
          </Link>
        }
        title={
          <span className="flex items-center gap-2.5">
            {fullName(target)}
            <Badge tone={roleTone(target.role)}>{humanize(target.role)}</Badge>
            {!target.active ? <Badge tone="neutral">Deactivated</Badge> : null}
          </span>
        }
        subtitle={
          <>
            Signs in as <Code>{target.username}</Code>
            {permissions.isSelf ? ' · this is your account' : ''}
          </>
        }
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<ArrowLeft className="size-3.5" />}
              asChild
            >
              <Link to="/system/staff" search={{ q: '', view: 'all' }}>
                Staff
              </Link>
            </Button>
            {dirty && !blocked ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDraft(null);
                    setPassword('');
                    setPin('');
                  }}
                >
                  Discard
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={invalid}
                  loading={save.isPending}
                  onClick={submit}
                >
                  Save changes
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <PageBody className="space-y-4">
        {!canWrite ? (
          <Callout tone="info" icon={<Lock />} title="Read-only">
            Editing staff needs the <Code>users.write</Code> capability, which starts at
            manager.
          </Callout>
        ) : permissions.blockedReason ? (
          <Callout
            tone="warn"
            icon={<ShieldAlert />}
            title="You cannot edit this account"
          >
            {permissions.blockedReason} That includes resetting their password or PIN —
            the server refuses it with a 403 regardless of what the console shows.
          </Callout>
        ) : null}

        <Panel title="Account">
          <DetailList columns={3}>
            <Detail label="User id" mono>
              <Code copyable>{target.id}</Code>
            </Detail>
            <Detail label="Username" mono>
              {target.username}
            </Detail>
            <Detail label="Status">
              {target.active ? (
                <Badge tone="positive" dot>
                  Active
                </Badge>
              ) : (
                <Badge tone="neutral">Deactivated</Badge>
              )}
            </Detail>
            <Detail label="Last sign-in" mono>
              {target.lastLoginAt
                ? `${relativeTime(target.lastLoginAt)} · ${dateTime(target.lastLoginAt)}`
                : 'never'}
            </Detail>
            <Detail label="Added" mono>
              {dateTime(target.createdAt)}
            </Detail>
            <Detail label="Role">
              <Badge tone={roleTone(target.role)}>{humanize(target.role)}</Badge>
            </Detail>
          </DetailList>
        </Panel>

        <Panel title="Profile" description="Saved together in one write.">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="First name" required>
              <Input
                value={value.firstName}
                disabled={blocked}
                invalid={value.firstName.trim().length === 0}
                onChange={(e) => setDraft({ ...value, firstName: e.currentTarget.value })}
              />
            </Field>
            <Field label="Last name">
              <Input
                value={value.lastName}
                disabled={blocked}
                onChange={(e) => setDraft({ ...value, lastName: e.currentTarget.value })}
              />
            </Field>

            <Field
              label="Role"
              hint={
                permissions.roleCeilingNote ??
                'Determines every capability this account has.'
              }
            >
              <Select
                value={value.role}
                disabled={blocked}
                onChange={(e) => setDraft({ ...value, role: e.currentTarget.value })}
              >
                {/* Always include the current role so an unreachable one is
                    still shown rather than silently swapped. */}
                {(permissions.roleOptions.some((role) => role === value.role)
                  ? [...permissions.roleOptions]
                  : [value.role, ...permissions.roleOptions]
                ).map((role) => (
                  <option key={role} value={role}>
                    {humanize(role)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Account access"
              hint={
                permissions.deactivateBlockedReason ??
                'A deactivated account cannot sign in to the console or the register.'
              }
            >
              <div className="flex h-8 items-center">
                <Switch
                  checked={value.active}
                  disabled={blocked || !permissions.canDeactivate}
                  onCheckedChange={(next) => setDraft({ ...value, active: next })}
                  label={value.active ? 'Active' : 'Deactivated'}
                />
              </div>
            </Field>
          </div>
        </Panel>

        <Panel
          title="Credentials"
          description="Both are write-only — the console never reads a hash back."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Reset password"
              error={password ? passwordError : null}
              hint="At least 4 characters. Leave empty to keep the current password."
            >
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                disabled={blocked}
                invalid={!!(password && passwordError)}
                placeholder="••••••••"
                onChange={(e) => setPassword(e.currentTarget.value)}
              />
            </Field>
            <Field
              label="Register PIN"
              error={pin ? pinError : null}
              hint="4 to 8 digits, unique across active users. Leave empty to keep the current PIN."
            >
              <Input
                mono
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                disabled={blocked}
                invalid={!!(pin && pinError)}
                placeholder="••••"
                onChange={(e) => setPin(e.currentTarget.value)}
              />
            </Field>
          </div>
          <p className="text-2xs text-ink-faint mt-3 flex items-center gap-1.5">
            <KeyRound className="size-3" />A PIN already used by another active account is
            refused with a 409 — pick a different one.
          </p>
        </Panel>
      </PageBody>

      <ConfirmDialog
        open={confirmDeactivate}
        onOpenChange={setConfirmDeactivate}
        title={`Deactivate ${fullName(target)}?`}
        description="They lose console and register access immediately."
        confirmLabel="Deactivate account"
        loading={save.isPending}
        consequences={[
          'Cannot sign in to the console or clock on at a register',
          'Their PIN stops matching, freeing it for reuse by someone else',
          'Historic transactions and shifts keep their name — nothing is deleted',
        ]}
        onConfirm={() => save.mutate(buildBody())}
      />
    </Page>
  );
}
